import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import dayjs from 'dayjs';
import {
  TableData,
  UniversalFilter,
  Status,
  Button,
  SideBarModal,
  ActionDropdown,
  ActionDropdownAction,
  ColumnSettings,
  Pagination,
} from '@/components';
import { useTableColumns } from '@/hooks/useTableColumns';
import { InputSearch } from '@/components/Input';
import { DatePicker } from '@/components/DatePicker';
import { FilePreviewCard } from '@components/FilePreviewCard';
import { showErrorToast, showSuccessToast } from '@/components/toast/globalToast';
import SummaryStat from '@/pages/vehicles/components/OwnedVehicle/SummaryStat';
import { Column } from '@/types/common';
import {
  stnkApi,
  DocumentType,
  StnkListFilters,
  StnkListItem,
  StnkStats,
  StnkStatus,
} from '@/services/api/stnk';
import {
  getBucketUrlFile,
  uploadTempFile,
  fileToBase64,
  downloadBlob,
} from '@/utils';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatExpiryDate(dateStr: string): string {
  return dayjs(dateStr).format('DD MMM YYYY');
}

function formatDaysRemaining(days: number): string {
  return days < 0 ? `${Math.abs(days)} hari yang lalu` : `${days} hari`;
}

function documentTypeLabel(type: DocumentType): string {
  return type === '5_tahunan' ? '5 Tahunan' : 'Tahunan';
}

// STNK list IDs are synthetic `${vehicleID}:${documentType}` strings (the STNK
// "document" is really columns on the vehicle row). The vehicle detail route
// takes the raw vehicle ID, so strip the trailing `:documentType`.
function vehicleIdFromDocumentId(id: string): string {
  const idx = id.lastIndexOf(':');
  return idx === -1 ? id : id.slice(0, idx);
}

// ─── Renewal drawer (shared SideBarModal) ──────────────────────────────────────

interface RenewalDrawerProps {
  item: StnkListItem;
  onClose: () => void;
  // The page refetches rather than patching the row, so the drawer has nothing
  // to hand back.
  onSuccess: () => void;
}

const RenewalDrawer: React.FC<RenewalDrawerProps> = ({
  item,
  onClose,
  onSuccess,
}) => {
  const [newExpiryDate, setNewExpiryDate] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [expiryError, setExpiryError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isFormReady = !!newExpiryDate && !!file;

  const handleSubmit = async () => {
    if (!isFormReady || loading) return;
    setExpiryError('');
    setLoading(true);

    try {
      // Upload to the shared temp bucket; the backend moves it into the
      // vehicle-scoped main bucket on renewal.
      const uuid = uuidv4();
      const fileFormat = file!.name.split('.').pop() || 'pdf';
      const bucketRes = await getBucketUrlFile({
        uuid,
        // Shared upload service only accepts a fixed file_type enum; STNK
        // documents ride in under 'vehicle' (see AddVehicleModal).
        fileType: 'vehicle',
        fileFormat,
      });
      if (!bucketRes?.data) {
        throw new Error('Failed to get upload URL');
      }
      const uploadUrl = bucketRes.data;
      const base64Data = await fileToBase64(file!);
      const uploadRes = await uploadTempFile(
        uploadUrl,
        base64Data,
        file!.name,
        file!.type
      );
      if (uploadRes.status !== 200) {
        throw new Error('Upload failed');
      }
      await stnkApi.saveRenewal(item.id, {
        new_expiry_date: dayjs(newExpiryDate).format('YYYY-MM-DD'),
        file_url: uploadUrl,
        notes: notes || null,
      });
      showSuccessToast('Berhasil', 'STNK berhasil diperbarui.');
      onSuccess();
    } catch (err: any) {
      // The API's global error filter flattens every error to
      // `{ status, error: <message> }`, dropping the machine-readable code —
      // so detect the expiry-date validation by its 422 status (the only
      // 422 this endpoint returns) and show it inline. Any other failure
      // surfaces the backend's own message rather than a blanket generic.
      const status = err?.response?.status;
      const backendError = err?.response?.data?.error;
      if (status === 422) {
        setExpiryError(
          'Tanggal berlaku baru harus setelah tanggal kadaluwarsa saat ini.'
        );
      } else {
        showErrorToast(
          'Error',
          backendError || 'Gagal menyimpan perpanjangan STNK.'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSelectFile = (f: File | null) => {
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      showErrorToast('File terlalu besar', 'Maksimal 10 MB.');
      return;
    }
    setFile(f);
  };

  return (
    <SideBarModal
      isOpen
      onClose={onClose}
      position="right"
      width="sm"
      header={
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            Upload Perpanjangan STNK
          </h2>
          <p className="text-sm font-normal text-gray-600">
            {item.plateNumber} · {item.oemName} {item.modelName} ·{' '}
            {documentTypeLabel(item.documentType)} · Exp{' '}
            {formatExpiryDate(item.expiryDate)}
          </p>
        </div>
      }
      footer={
        <div className="flex flex-row gap-3">
          <Button
            variant="white"
            className="w-full py-3 !border !border-gray-300"
            onClick={onClose}
          >
            Batal
          </Button>
          <Button
            variant="primary"
            className="w-full py-3"
            disabled={!isFormReady || loading}
            onClick={handleSubmit}
          >
            {loading && <i className="ri-loader-4-line animate-spin mr-2" />}
            Simpan & Reset Pengingat
          </Button>
        </div>
      }
    >
      <div className="space-y-4 px-6 py-4">
        {item.documentType === '5_tahunan' && (
          <div className="p-3 bg-[#EFEAF7] border border-[#5E2AAC]/20 rounded-lg flex gap-2">
            <i className="ri-information-line text-[#5E2AAC] mt-0.5 shrink-0" />
            <p className="text-sm text-[#5E2AAC]">
              <strong>5 Tahunan</strong> memerlukan kunjungan fisik ke kantor
              Samsat dan penggantian plat kendaraan. Siapkan BPKB asli.
            </p>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tanggal Berlaku STNK Baru <span className="text-red-500">*</span>
          </label>
          <DatePicker
            value={newExpiryDate}
            onChange={setNewExpiryDate}
            placeholder="Pilih tanggal berlaku baru"
            showTimeSelect={false}
            error={!!expiryError}
            className="w-full"
          />
          {expiryError && (
            <p className="text-xs text-red-500 mt-1">{expiryError}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Upload Scan STNK Baru <span className="text-red-500">*</span>
          </label>
          {file ? (
            <FilePreviewCard
              fileName={file.name}
              fileSize={file.size}
              showStatus={false}
              onRemove={() => setFile(null)}
            />
          ) : (
            <div
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-[#5E2AAC] transition-colors"
            >
              <i className="ri-upload-cloud-2-line text-2xl block mb-1 text-gray-400" />
              <span className="text-sm text-gray-400">
                Klik untuk upload PDF (maks. 10 MB)
              </span>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            className="hidden"
            onChange={(e) => {
              handleSelectFile(e.target.files?.[0] || null);
              e.target.value = '';
            }}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Catatan
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Opsional — catatan perpanjangan"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#5E2AAC] resize-none"
          />
        </div>
      </div>
    </SideBarModal>
  );
};

// ─── Deactivate confirmation (shared SideBarModal, centered) ────────────────────

interface DeactivateDialogProps {
  item: StnkListItem;
  loading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

const DeactivateDialog: React.FC<DeactivateDialogProps> = ({
  item,
  loading,
  onCancel,
  onConfirm,
}) => (
  <SideBarModal
    isOpen
    onClose={onCancel}
    position="center"
    width="sm"
    header={
      <h2 className="text-lg font-semibold text-gray-900">
        Nonaktifkan Pengingat STNK
      </h2>
    }
    footer={
      <div className="flex flex-row gap-3">
        <Button
          variant="white"
          className="w-full py-2.5 !border !border-gray-300"
          onClick={onCancel}
        >
          Batal
        </Button>
        <Button
          variant="danger"
          className="w-full py-2.5"
          disabled={loading}
          onClick={onConfirm}
        >
          {loading && <i className="ri-loader-4-line animate-spin mr-2" />}
          Ya, Non-Aktifkan
        </Button>
      </div>
    }
  >
    <div className="px-6 py-4">
      <p className="text-sm text-gray-700">
        Nonaktifkan pengingat STNK untuk{' '}
        <span className="font-semibold">{item.plateNumber}</span>? Tanggal
        berlaku dan dokumen STNK{' '}
        {documentTypeLabel(item.documentType).toLowerCase()} akan dihapus,
        kendaraan keluar dari daftar, dan pengingat Slack dihentikan. Tindakan
        ini tidak bisa dibatalkan.
      </p>
    </div>
  </SideBarModal>
);

// ─── Main Page ──────────────────────────────────────────────────────────────

const StnkManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<StnkStats | null>(null);
  const [items, setItems] = useState<StnkListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionDropdownOpen, setActionDropdownOpen] = useState<string | null>(
    null
  );

  const [view, setView] = useState<'attention' | 'all'>('all');
  const [typeFilter, setTypeFilter] = useState<DocumentType | ''>('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [sortField, setSortField] = useState('daysRemaining');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const [uploadTarget, setUploadTarget] = useState<StnkListItem | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<StnkListItem | null>(
    null
  );
  const [deactivateLoading, setDeactivateLoading] = useState(false);

  // Filters shared by the list request and the CSV export, so the downloaded
  // file always matches the table on screen. Memoised so it can be a dependency
  // rather than being spelled out twice.
  const listFilters: StnkListFilters = useMemo(
    () => ({
      view,
      type: typeFilter || undefined,
      search: search || undefined,
      sort: sortField,
      order: sortDirection,
    }),
    [view, typeFilter, search, sortField, sortDirection]
  );

  const fetchStats = useCallback(async () => {
    try {
      setStats(await stnkApi.getStats());
    } catch {
      showErrorToast('Error', 'Gagal memuat ringkasan STNK.');
    }
  }, []);

  // Only the newest request may touch state: a slow response from a filter the
  // user has already moved on from would otherwise paint over a newer one.
  const latestRequestID = useRef(0);
  const fetchList = useCallback(async () => {
    const requestID = ++latestRequestID.current;
    const isCurrent = () => requestID === latestRequestID.current;
    setLoading(true);
    try {
      const result = await stnkApi.getList({
        ...listFilters,
        page: currentPage,
        size: itemsPerPage,
      });
      if (!isCurrent()) return;
      setItems(result.data);
      setTotalItems(result.pagination.total);
    } catch {
      if (!isCurrent()) return;
      // Leaving the previous filter's rows on screen under the new filter reads
      // as data, so clear them.
      setItems([]);
      setTotalItems(0);
      showErrorToast('Error', 'Gagal memuat data STNK.');
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [listFilters, currentPage, itemsPerPage]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // Close the row action menu on any outside click (mirrors OwnedVehicleTab).
  useEffect(() => {
    if (actionDropdownOpen === null) return;
    const handleClickOutside = () => setActionDropdownOpen(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [actionDropdownOpen]);

  const goToVehicleDetail = useCallback(
    (item: StnkListItem) => {
      navigate(`/vehicles/${vehicleIdFromDocumentId(item.id)}`);
    },
    [navigate]
  );

  const handleRenewalSuccess = () => {
    setUploadTarget(null);
    // A renewal can move the row off the current page entirely (it may no
    // longer be "perlu perhatian", and the sort key changed), so refetch rather
    // than patching the row in place. Same for the cards, which span the whole
    // fleet rather than the loaded page.
    Promise.all([fetchList(), fetchStats()]);
  };

  const handleDeactivate = async () => {
    if (!deactivateTarget) return;
    const id = deactivateTarget.id;
    setDeactivateLoading(true);
    try {
      await stnkApi.deactivate(id);
      showSuccessToast('Berhasil', 'Kendaraan berhasil dinonaktifkan.');
      setDeactivateTarget(null);
      fetchStats();
      if (items.length === 1 && currentPage > 1) {
        // That was the last row on this page; stepping back re-runs the fetch
        // effect, so don't also fetch here.
        setCurrentPage(currentPage - 1);
      } else {
        await fetchList();
      }
    } catch {
      showErrorToast('Error', 'Gagal menonaktifkan kendaraan.');
    } finally {
      setDeactivateLoading(false);
    }
  };

  const handleDownloadCsv = async () => {
    try {
      const blob = await stnkApi.downloadCsv(listFilters);
      downloadBlob(`stnk_${dayjs().format('YYYY-MM-DD')}.csv`, blob);
      showSuccessToast('Berhasil', 'Download CSV dimulai.');
    } catch (err: any) {
      showErrorToast('Error', err?.message || 'Gagal mengunduh CSV.');
    }
  };

  // UniversalFilter config — both groups are single-select, matching what the
  // API accepts. `view` offers only the narrowing option: no selection means
  // every vehicle, so an explicit "Semua Kendaraan" choice would be a no-op.
  const filterConfigs = [
    {
      key: 'type',
      title: 'Tipe STNK',
      icon: 'ri-file-list-3-line',
      multiple: false,
      staticOptions: [
        { key: 'tahunan', label: 'Tahunan', value: 'tahunan' },
        { key: '5_tahunan', label: '5 Tahunan', value: '5_tahunan' },
      ],
    },
    {
      key: 'view',
      title: 'Tampilan',
      icon: 'ri-eye-line',
      multiple: false,
      staticOptions: [
        { key: 'attention', label: 'Perlu Perhatian', value: 'attention' },
      ],
    },
  ];

  const columns: Column<StnkListItem>[] = [
    {
      key: 'plateNumber',
      title: 'Nomor Plat',
      sortable: true,
      render: (value: string) => (
        <span className="text-[14px] font-bold text-gray-900">{value}</span>
      ),
    },
    {
      key: 'oemName',
      title: 'OEM Name',
      sortable: true,
      render: (value: string) => (
        <span className="text-[14px] text-gray-600">{value}</span>
      ),
    },
    {
      key: 'modelName',
      title: 'Model Name',
      sortable: true,
      render: (value: string) => (
        <span className="text-[14px] text-gray-600">{value}</span>
      ),
    },
    {
      key: 'documentType',
      title: 'Tipe STNK',
      sortable: true,
      render: (value: DocumentType) => (
        <span className="text-[14px] text-gray-600">
          {documentTypeLabel(value)}
        </span>
      ),
    },
    {
      key: 'expiryDate',
      title: 'Tgl Exp.',
      sortable: true,
      render: (value: string) => (
        <span className="text-[14px] text-gray-600">
          {formatExpiryDate(value)}
        </span>
      ),
    },
    {
      key: 'daysRemaining',
      title: 'Sisa Hari',
      sortable: true,
      render: (value: number) => (
        <span className="text-[14px] font-medium text-gray-900">
          {formatDaysRemaining(value)}
        </span>
      ),
    },
    {
      key: 'status',
      title: 'Status',
      sortable: true,
      render: (value: StnkStatus) => (
        <Status value={value} type="stnk" size="small" />
      ),
    },
    {
      key: 'actions',
      title: '',
      align: 'right' as const,
      render: (_value: unknown, record: StnkListItem) => {
        const actions: ActionDropdownAction[] = [
          {
            key: 'detail',
            label: 'Detail',
            icon: 'ri-eye-line',
            onClick: () => goToVehicleDetail(record),
          },
        ];
        return (
          <div className="flex items-center justify-end gap-2">
            {record.status !== 'aman' && (
              <Button
                variant="primary"
                size="small"
                onClick={() => setUploadTarget(record)}
              >
                Upload
              </Button>
            )}
            {record.status === 'kadaluwarsa' && (
              <Button
                variant="danger"
                size="small"
                onClick={() => setDeactivateTarget(record)}
              >
                Non-Aktif
              </Button>
            )}
            <ActionDropdown
              record={record}
              isOpen={actionDropdownOpen === record.id}
              onToggle={() =>
                setActionDropdownOpen((prev) =>
                  prev === record.id ? null : record.id
                )
              }
              actions={actions}
              ariaLabel={`Actions for ${record.plateNumber}`}
            />
          </div>
        );
      },
    },
  ];

  // Column customization (show/hide + reorder), persisted per browser.
  const { visibleColumns, columnSettingsProps } = useTableColumns<StnkListItem>({
    storageKey: 'stnk',
    columns,
  });

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            STNK Management
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Pantau dan kelola perpanjangan STNK armada
          </p>
        </div>
        <Button
          variant="primary"
          size="small"
          onClick={handleDownloadCsv}
          disabled={!totalItems}
        >
          <i className="ri-download-line text-[12px] mr-1" />
          Download CSV
        </Button>
      </div>

      {/* Summary cards */}
      <section aria-labelledby="stnk-summary" className="mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-start">
          <SummaryStat
            title="Total STNK"
            value={stats?.total ?? 0}
            icon={<i className="ri-roadster-line" />}
          />
          <SummaryStat
            title="Kadaluwarsa"
            value={stats?.expired ?? 0}
            icon={<i className="ri-alarm-warning-line" />}
          />
          <SummaryStat
            title="Dalam 45 Hari"
            value={stats?.within45d ?? 0}
            icon={<i className="ri-time-line" />}
          />
          <SummaryStat
            title="Aman"
            value={stats?.safe ?? 0}
            icon={<i className="ri-shield-check-line" />}
          />
        </div>
      </section>

      {/* Search + Filter */}
      <div className="rounded-lg p-2 mb-4">
        <div className="flex items-center justify-end gap-2">
          <InputSearch
            placeholder="Cari nomor plat..."
            value={searchInput}
            onChange={setSearchInput}
            onSearch={(value: string) => {
              setSearch(value);
              setCurrentPage(1);
            }}
            onClear={() => {
              setSearchInput('');
              setSearch('');
              setCurrentPage(1);
            }}
            debounceMs={300}
            size="small"
          />
          <UniversalFilter
            filters={filterConfigs}
            currentFilters={{
              ...(typeFilter ? { type: typeFilter } : {}),
              ...(view === 'attention' ? { view: 'attention' } : {}),
            }}
            onApply={(filters: Record<string, any>) => {
              setTypeFilter((filters.type as DocumentType) || '');
              setView(filters.view === 'attention' ? 'attention' : 'all');
              setCurrentPage(1);
            }}
            onClear={() => {
              setTypeFilter('');
              setView('all');
              setCurrentPage(1);
            }}
          />
          <ColumnSettings {...columnSettingsProps} />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#5E2AAC]" />
            <span className="ml-2 text-gray-600">Memuat data STNK...</span>
          </div>
        ) : (
          <TableData
            data={items}
            columns={visibleColumns}
            size="small"
            className="min-w-full"
            onRowClick={goToVehicleDetail}
            sorting={{
              field: sortField,
              direction: sortDirection,
              // Sorting must run server-side: the table only holds one page, so
              // sorting locally would reorder ten rows and call it the fleet.
              // TableData cycles asc -> desc -> null, but "unsorted" has no
              // server equivalent (the query always orders by something), so
              // the third click wraps back to asc on the same column.
              onSort: (field, direction) => {
                setSortField(field);
                setSortDirection(direction ?? 'asc');
                setCurrentPage(1);
              },
            }}
          />
        )}
      </div>

      <Pagination
        currentPage={currentPage}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        onPageChange={setCurrentPage}
        onPageSizeChange={(size: number) => {
          setItemsPerPage(size);
          setCurrentPage(1);
        }}
      />

      {/* Renewal drawer */}
      {uploadTarget && (
        <RenewalDrawer
          item={uploadTarget}
          onClose={() => setUploadTarget(null)}
          onSuccess={handleRenewalSuccess}
        />
      )}

      {/* Deactivate confirmation */}
      {deactivateTarget && (
        <DeactivateDialog
          item={deactivateTarget}
          loading={deactivateLoading}
          onCancel={() => setDeactivateTarget(null)}
          onConfirm={handleDeactivate}
        />
      )}
    </div>
  );
};

export default StnkManagementPage;
